import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Check, X, ShieldCheck, ClipboardCheck, UserPlus } from 'lucide-react';
import { LoginInvitesCard } from '@/components/LoginInvitesCard';
import { ActivityCard } from '@/components/ActivityCard';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75', bg: '#F4F6F9' };

function TeamLoginsCard({ api }) {
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'underwriter' });
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/platform/users').then(r => setUsers(r.data.users)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post('/platform/users', form);
      toast.success(r.data.message);
      setForm({ email: '', name: '', password: '', role: 'underwriter' });
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not create login');
    }
    setBusy(false);
  };

  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="team-logins-card">
      <div className="flex items-center gap-2 mb-1">
        <UserPlus className="w-4 h-4" style={{ color: C.blue }} />
        <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Team Logins</h3>
      </div>
      <p className="text-[11px] mb-3" style={{ color: C.slate }}>Create staff logins (registration is invite-only). Broker logins are issued from the Distribution module.</p>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@company.com" data-testid="team-login-email"
          className="px-2 py-1.5 text-xs rounded border flex-1 min-w-[190px]" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} />
        <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="team-login-name"
          className="px-2 py-1.5 text-xs rounded border w-40" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} />
        <input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Password (min 8)" type="text" data-testid="team-login-password"
          className="px-2 py-1.5 text-xs rounded border w-40" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} />
        <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} data-testid="team-login-role"
          className="px-2 py-1.5 text-xs rounded border" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }}>
          <option value="underwriter">Underwriter</option>
          <option value="mga_admin">MGA Admin</option>
        </select>
        <Button onClick={create} disabled={busy || !form.email || form.password.length < 8} data-testid="team-login-create"
          className="text-xs rounded-md text-white h-8" style={{ background: C.blue }}>Create login</Button>
      </div>
      {users && users.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {users.map(u => (
            <span key={u.email} className="px-2 py-1 rounded-sm text-[10px]" data-testid={`team-user-${u.email}`}
              style={{ background: C.bg, color: C.navy }}>
              {u.name || u.email} · <b style={{ color: C.blue }}>{u.role}</b>
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function AccessControlPage() {
  const api = useApi();
  const [m, setM] = useState(null);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attesting, setAttesting] = useState(false);

  const loadReview = () => api.get('/access-review').then(r => setReview(r.data)).catch(() => {});
  useEffect(() => {
    Promise.all([
      api.get('/permission-matrix').then(r => setM(r.data)).catch(e => console.error(e?.message)),
      loadReview(),
    ]).finally(() => setLoading(false));
  }, [api]);

  const attest = () => {
    setAttesting(true);
    api.post('/access-review/attest', { note: 'Access review confirmed' })
      .then(() => { toast.success('Access review attested'); loadReview(); })
      .catch(() => toast.error('Attestation failed'))
      .finally(() => setAttesting(false));
  };

  if (loading) return <Skeleton className="h-96 rounded-md" />;
  if (!m) return <div className="text-sm" style={{ color: C.slate }}>Unable to load permission matrix.</div>;

  const perms = m.permissions || [];
  const roles = m.roles || [];
  const has = (role, key) => roles.find(r => r.role === role)?.granted?.includes(key);
  const last = review?.latest;

  return (
    <div className="space-y-4" data-testid="access-control-page">
      <TeamLoginsCard api={api} />
      <ActivityCard api={api} />
      <LoginInvitesCard api={api} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" style={{ color: C.blue }} />
          <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Role → Permission Matrix</h3>
          <span className="text-xs px-2 py-0.5 rounded" style={{ background: `${C.blue}12`, color: C.blue }}>your role: {m.your_role}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px]" style={{ color: C.slate }} data-testid="last-reviewed">
            {last ? `Last reviewed: ${new Date(last.attested_at).toLocaleDateString('en-AU')} by ${last.reviewer}` : 'Never reviewed'}
          </span>
          <Button size="sm" onClick={attest} disabled={attesting} data-testid="attest-btn" style={{ background: C.navy, color: 'white' }}>
            <ClipboardCheck className="w-4 h-4 mr-1.5" />{attesting ? 'Attesting…' : 'Attest Review'}
          </Button>
        </div>
      </div>
      <p className="text-xs" style={{ color: C.slate }}>Read-only capability map enforced by <code>require_permission()</code> (SOC 2 CC6.1–6.3). Source of truth: <code>core/permissions.py</code>. Periodic attestation satisfies the access-review control.</p>
      <Card className="rounded-md overflow-x-auto" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid="matrix-table">
        <table className="w-full text-xs">
          <thead>
            <tr style={{ background: C.bg }}>
              <th className="text-left px-3 py-2 font-semibold sticky left-0" style={{ color: C.navy, background: C.bg }}>Capability</th>
              {roles.map(r => (
                <th key={r.role} className="px-3 py-2 font-semibold text-center whitespace-nowrap" style={{ color: r.role === m.your_role ? C.blue : C.navy }} data-testid={`col-${r.role}`}>
                  {r.role.replace(/_/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {perms.map(p => (
              <tr key={p.key} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`row-${p.key}`}>
                <td className="px-3 py-2 sticky left-0 bg-white" style={{ color: C.navy }}>
                  <span className="font-mono text-[10px]" style={{ color: C.slate }}>{p.key}</span>
                  <div className="text-[11px]">{p.label}</div>
                </td>
                {roles.map(r => (
                  <td key={r.role} className="px-3 py-2 text-center" style={{ background: r.role === m.your_role ? `${C.blue}08` : 'transparent' }}>
                    {has(r.role, p.key)
                      ? <Check className="w-4 h-4 mx-auto" style={{ color: C.green }} />
                      : <X className="w-3.5 h-3.5 mx-auto" style={{ color: 'rgba(136,150,168,0.4)' }} />}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
